import { useMemo } from 'react'
import type { MutableRefObject } from 'react'
import * as THREE from 'three'
import { Merkplaat } from './onderdelen'
import type { TruckKleuren } from './onderdelen'
import { Tussen, useMaticMaterialen } from './bouwdelen'
import type { Mat } from './bouwdelen'

/**
 * C-MATIC 10 — plat autonoom transportvoertuig (serie 8925) dat onder een
 * lading rijdt, hem 60 mm optilt en wegbrengt.
 *
 * ## Maten
 *
 * Uit het VDI-typeblad: l1 1180 (rijrichting, z), b1 832, h13 260 gezakt,
 * heffing h3 60, aandrijfwielen op spoor 758, en een plaat van 1200 × 887;
 * een europallet (800 × 1200) staat erop met de 1200 dwars.
 *
 * De vorm tussen die maten komt van de draaitafel op linde-mh.com (36
 * renders, zie `docs/linde-draaitafels.md`), op 1920 px:
 *
 *  - een **rode kuip** met afgeschuinde hoeken en een schuine bovenrand, rood
 *    tot op de bumper aan de lange zijden;
 *  - **zwarte kopse kanten** met het Linde-woordmerk, een noodstop, de
 *    M/A-schakelaar en in de hoek het venster van de veiligheidsscanner;
 *  - onderlangs een **bumper van drie zachte rubberbanden**;
 *  - op de lange zijden de **wielkap** van het aandrijfwiel en bij één kop
 *    een zwart paneel met het merk, een noodstop en de aansluitingen;
 *  - bovenop twee **lidars** op diagonale hoeken;
 *  - de **plaat** donker, met afgeronde schuine hoeken, een verzonken
 *    draaischijf in het midden en gaten voor de adapters — geen vangblokken.
 *
 * Lokaal +z is vooruit. `hef` is de heffing in meters (0 tot `C_HEF`); de
 * route zet hem per frame.
 */

export const C_LENGTE = 1.18
export const C_BREEDTE = 0.832
/** Bovenkant van de plaat, gezakt, en de heffing. */
export const C_HOOGTE = 0.26
export const C_HEF = 0.06

/** Achthoekig grondvlak met afgeschuinde hoeken, in (x, z). */
function achthoek(l: number, b: number, schuin: number) {
  const v = new THREE.Shape()
  const x = b / 2
  const z = l / 2
  v.moveTo(-x + schuin, -z)
  v.lineTo(x - schuin, -z)
  v.lineTo(x, -z + schuin)
  v.lineTo(x, z - schuin)
  v.lineTo(x - schuin, z)
  v.lineTo(-x + schuin, z)
  v.lineTo(-x, z - schuin)
  v.lineTo(-x, -z + schuin)
  v.closePath()
  return v
}

/** Plat stuk met achthoekig grondvlak, van `y0` tot `y1`. */
function plaatGeometrie(l: number, b: number, schuin: number, y0: number, y1: number, rond = 0.012) {
  const g = new THREE.ExtrudeGeometry(achthoek(l - rond * 2, b - rond * 2, schuin), {
    depth: y1 - y0 - rond * 2, bevelEnabled: true, bevelThickness: rond, bevelSize: rond, bevelSegments: 2,
  })
  // De vorm staat in (x, z) en wordt langs zijn eigen z geëxtrudeerd.
  // rotateX(π/2) legt hem plat: vormas y wordt z en de extrusie wijst omlaag;
  // daarna omhoog tot de bovenkant op y1 ligt.
  g.rotateX(Math.PI / 2)
  g.translate(0, y1 - rond, 0)
  return g
}

function useCMaterialen(kleuren: TruckKleuren) {
  const m = useMaticMaterialen(kleuren)
  return useMemo(() => ({
    ...m,
    // Poedercoating: donkergrijs, zijdemat — op de renders duidelijk lichter
    // dan het rubber eronder.
    plaat: new THREE.MeshStandardMaterial({ color: '#35373a', roughness: 0.62, metalness: 0.12 }),
    schijf: new THREE.MeshStandardMaterial({ color: '#2a2b2e', roughness: 0.5, metalness: 0.2 }),
    kolom: new THREE.MeshStandardMaterial({ color: '#8d9197', roughness: 0.35, metalness: 0.75 }),
    rubber: new THREE.MeshStandardMaterial({ color: '#141517', roughness: 0.82, metalness: 0.02 }),
    // De zwarte kopdelen zijn kunststof met een lichte glans, geen rubber.
    kop: new THREE.MeshPhysicalMaterial({ color: '#17181b', roughness: 0.4, metalness: 0.05, clearcoat: 0.4, clearcoatRoughness: 0.3 }),
    gat: new THREE.MeshBasicMaterial({ color: '#0b0b0c' }),
  }), [m])
}

type CMat = Mat & ReturnType<typeof useCMaterialen>

/* ------------------------------------------------------------------ maten */

/** Halve lengte en breedte, en de afschuining van de hoeken in bovenaanzicht. */
const HL = C_LENGTE / 2
const HB = C_BREEDTE / 2
const SCHUIN = 0.13
/**
 * Onderkant en bovenkant van de kuip, en de hoogte van de schuine rand.
 * Opgemeten op het vooraanzicht (frame 15, 1920 px, 1325 px/m): bumper tot
 * 62 mm, zwart kopvlak tot 175, rode rand tot 216, dan 28 mm spleet en een
 * plaat van 15 mm.
 */
const KUIP_Y0 = 0.062
const KUIP_Y1 = 0.216
const RAND = 0.028

/**
 * Kuip met een schuine bovenrand: een extrusie met één afschuinsegment. De
 * afschuining zit onder én boven, maar onder valt hij in de bumper.
 */
function kuipGeometrie() {
  const g = new THREE.ExtrudeGeometry(achthoek(C_LENGTE - 2 * RAND, C_BREEDTE - 2 * RAND, SCHUIN - RAND * 0.8), {
    depth: KUIP_Y1 - KUIP_Y0 - 2 * RAND, bevelEnabled: true, bevelThickness: RAND, bevelSize: RAND, bevelSegments: 1,
  })
  g.rotateX(Math.PI / 2)
  g.translate(0, KUIP_Y1 - RAND, 0)
  return g
}

/** Een rubberband van de bumper: rond van profiel, iets buiten de kuip. */
const band = (y: number) => plaatGeometrie(C_LENGTE + 0.022, C_BREEDTE + 0.022, SCHUIN + 0.01, y, y + 0.022, 0.01)

/* ------------------------------------------------------------------ kop */

/**
 * Zwarte kopse kant, voor (kant 1) of achter (−1): het woordmerk in het
 * midden, rechts de noodstop en de M/A-schakelaar, links een camera, onder
 * een verzonken venster; in de rechterhoek het scannervenster.
 */
function Kop({ m, kant }: { m: CMat; kant: 1 | -1 }) {
  const rot: [number, number, number] = [0, kant === 1 ? 0 : Math.PI, 0]
  const vlak = HB - SCHUIN
  // De schuine hoekvlakken: van (vlak, HL) naar (HB, HL − SCHUIN).
  const hoekLengte = Math.hypot(SCHUIN, SCHUIN)
  return (
    <group rotation={rot}>
      {/* Het zwarte kopvlak over de hele breedte en de twee hoeken. */}
      <Tussen a={[-vlak, KUIP_Y0, HL - 0.012]} b={[vlak, KUIP_Y1 - RAND - 0.012, HL - 0.002]} m={m.kop} />
      {[-1, 1].map((k) => (
        <group key={k} position={[k * (vlak + SCHUIN / 2), 0, HL - SCHUIN / 2]} rotation={[0, k * Math.PI / 4, 0]}>
          <Tussen a={[-hoekLengte / 2, KUIP_Y0, -0.009]} b={[hoekLengte / 2, KUIP_Y1 - RAND - 0.012, 0.003]} m={m.kop} />
          {k === 1 && (
            // Scannervenster: een donker rooster, iets verzonken.
            <Tussen a={[-0.045, 0.085, 0.003]} b={[0.045, 0.15, 0.006]} m={m.gat} schaduw={false} />
          )}
        </group>
      ))}
      <group position={[0, 0, HL]}>
        <Merkplaat positie={[-0.02, 0.135, 0.0035]} breedte={0.15} type="" beugel={false} />
        {/* Verzonken venster onder het woordmerk. */}
        <Tussen a={[-0.07, 0.078, 0.001]} b={[0.05, 0.1, 0.004]} m={m.gat} schaduw={false} />
        {/* Camera links. */}
        <mesh position={[-0.2, 0.135, 0.004]} rotation={[Math.PI / 2, 0, 0]} material={m.gat}>
          <cylinderGeometry args={[0.012, 0.012, 0.004, 16]} />
        </mesh>
        {/* M/A-schakelaar en een verklikker. */}
        <mesh position={[0.15, 0.14, 0.006]} rotation={[Math.PI / 2, 0, 0]} material={m.chassis}>
          <cylinderGeometry args={[0.012, 0.012, 0.008, 16]} />
        </mesh>
        <mesh position={[0.115, 0.14, 0.005]} rotation={[Math.PI / 2, 0, 0]} material={m.kolom}>
          <cylinderGeometry args={[0.006, 0.006, 0.006, 12]} />
        </mesh>
        {/* Noodstop: gele kraag, rode paddenstoel. */}
        <mesh position={[0.21, 0.125, 0.006]} rotation={[Math.PI / 2, 0, 0]} material={m.geel}>
          <cylinderGeometry args={[0.028, 0.028, 0.008, 20]} />
        </mesh>
        <mesh position={[0.21, 0.125, 0.018]} rotation={[Math.PI / 2, 0, 0]} material={m.noodstop}>
          <cylinderGeometry args={[0.019, 0.021, 0.018, 18]} />
        </mesh>
      </group>
    </group>
  )
}

/* ------------------------------------------------------------------ zijkant */

/** Zwart paneel bij de kop, op de lange zijde: een schuine vorm met merk en bediening. */
function zijpaneelVorm(d: number) {
  const v = new THREE.Shape()
  // In (u, y), u vanaf het midden van de zijde naar de kop, maal `d`: de
  // vorm moet per zijde anders om liggen, anders kijkt zijn (enige) voorkant
  // naar binnen.
  v.moveTo(d * 0.2, 0.07)
  v.lineTo(d * (HL - SCHUIN + 0.005), 0.07)
  v.lineTo(d * (HL - SCHUIN + 0.005), 0.175)
  v.lineTo(d * 0.3, 0.175)
  v.quadraticCurveTo(d * 0.255, 0.175, d * 0.235, 0.14)
  v.closePath()
  return v
}

function Zijkant({ m, k }: { m: CMat; k: 1 | -1 }) {
  // Lokaal +x van het paneel wijst na de draaiing om y naar −z bij k = 1 en
  // naar +z bij k = −1; de voorkop ligt aan +z.
  const d = -k
  const paneel = useMemo(() => new THREE.ShapeGeometry(zijpaneelVorm(d)), [d])
  return (
    <group>
      {/* Wielkap van het aandrijfwiel (Ø 200) midden op de zijde: een zwarte
          schijf die tot in de bumper reikt, met een binnenring en zes bouten. */}
      <group position={[k * (HB + 0.004), 0.1, 0]} rotation={[0, 0, Math.PI / 2]}>
        <mesh material={m.kop} castShadow>
          <cylinderGeometry args={[0.095, 0.095, 0.016, 40]} />
        </mesh>
        <mesh position={[0, k * 0.009, 0]} material={m.schijf}>
          <cylinderGeometry args={[0.058, 0.058, 0.004, 32]} />
        </mesh>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2 + 0.3
          return (
            <mesh key={i} position={[Math.cos(a) * 0.074, k * 0.009, Math.sin(a) * 0.074]} material={m.gat}>
              <cylinderGeometry args={[0.005, 0.005, 0.003, 8]} />
            </mesh>
          )
        })}
      </group>

      {/* Zwart paneel bij de voorkop (+z), op beide zijden. Het lokale +z van
          de groep wijst naar buiten; `d` legt de vorm naar de voorkop. */}
      <group position={[k * (HB + 0.002), 0, 0]} rotation={[0, k * Math.PI / 2, 0]}>
        <mesh geometry={paneel} material={m.kop} />
        <Merkplaat positie={[d * 0.33, 0.14, 0.002]} breedte={0.1} type="C-MATIC" beugel={false} />
        {/* Noodstop en drie aansluitingen. */}
        <mesh position={[d * 0.39, 0.11, 0.006]} rotation={[Math.PI / 2, 0, 0]} material={m.geel}>
          <cylinderGeometry args={[0.019, 0.019, 0.006, 18]} />
        </mesh>
        <mesh position={[d * 0.39, 0.11, 0.014]} rotation={[Math.PI / 2, 0, 0]} material={m.noodstop}>
          <cylinderGeometry args={[0.013, 0.014, 0.012, 16]} />
        </mesh>
        {[0.42, 0.44].map((z) => (
          <mesh key={z} position={[d * z, 0.11, 0.003]} rotation={[Math.PI / 2, 0, 0]} material={m.gat}>
            <cylinderGeometry args={[0.008, 0.008, 0.004, 14]} />
          </mesh>
        ))}
      </group>
    </group>
  )
}

/* ------------------------------------------------------------------ de machine */

/** Paren boorgaten in de plaat, voor de adapters: (x, z). */
const GATEN: [number, number][] = [
  [-0.3, -0.38], [0.3, -0.38], [-0.3, 0.38], [0.3, 0.38],
  [-0.18, 0], [0.18, 0], [0, -0.24], [0, 0.24], [-0.36, 0.05], [0.36, -0.05],
]

export function CMatic({ kleuren, hef }: { kleuren: TruckKleuren; hef: MutableRefObject<THREE.Group | null> }) {
  const m = useCMaterialen(kleuren) as CMat
  const geo = useMemo(() => ({
    kuip: kuipGeometrie(),
    banden: [0.003, 0.022, 0.041].map(band),
    kern: plaatGeometrie(C_LENGTE, C_BREEDTE, SCHUIN, 0.004, KUIP_Y0 + 0.01, 0.003),
    plaat: plaatGeometrie(1.2, 0.887, 0.12, C_HOOGTE - 0.016, C_HOOGTE, 0.007),
  }), [])

  return (
    <group>
      {/* De kuip, rood tot op de bumper; de kopse kanten zwart. */}
      <mesh geometry={geo.kuip} material={m.lakRood} castShadow receiveShadow />
      <Kop m={m} kant={1} />
      <Kop m={m} kant={-1} />
      {/* Bumper: drie zachte rubberbanden om een zwarte kern. */}
      <mesh geometry={geo.kern} material={m.rubber} />
      {geo.banden.map((g, i) => (
        <mesh key={i} geometry={g} material={m.rubber} castShadow={i === 0} receiveShadow />
      ))}

      <Zijkant m={m} k={1} />
      <Zijkant m={m} k={-1} />

      {/* Twee lidars op diagonale hoeken, elk op een zwart voetstuk. */}
      {[1, -1].map((k) => (
        <group key={k} position={[k * 0.3, KUIP_Y1, k * 0.45]}>
          <Tussen a={[-0.06, -0.004, -0.06]} b={[0.06, 0.004, 0.06]} m={m.kop} />
          <mesh position={[0, 0.032, 0]} material={m.kop} castShadow>
            <cylinderGeometry args={[0.046, 0.05, 0.056, 28]} />
          </mesh>
          <mesh position={[0, 0.03, 0]} material={m.glas}>
            <cylinderGeometry args={[0.0505, 0.0505, 0.022, 28]} />
          </mesh>
        </group>
      ))}

      {/* De hefkolom tussen kuip en plaat: glanzend staal, in de spleet te
          zien. Hij schuift uit: de binnenste buis gaat met de plaat mee. */}
      <mesh position={[0, KUIP_Y1 + 0.006, 0]} material={m.kolom}>
        <cylinderGeometry args={[0.2, 0.2, 0.016, 40]} />
      </mesh>
      <group ref={hef}>
        <mesh position={[0, C_HOOGTE - 0.016 - 0.04, 0]} material={m.kolom}>
          <cylinderGeometry args={[0.185, 0.185, 0.08, 40]} />
        </mesh>
        {/* De plaat, 60 mm op en neer: verzonken draaischijf, gat in het
            midden, boorgaten voor de adapters. */}
        <mesh geometry={geo.plaat} material={m.plaat} castShadow receiveShadow />
        <mesh position={[0, C_HOOGTE + 0.0006, 0]} rotation={[-Math.PI / 2, 0, 0]} material={m.schijf}>
          <ringGeometry args={[0.075, 0.25, 48]} />
        </mesh>
        <mesh position={[0, C_HOOGTE + 0.0008, 0]} rotation={[-Math.PI / 2, 0, 0]} material={m.plaat}>
          <ringGeometry args={[0.25, 0.262, 48]} />
        </mesh>
        <mesh position={[0, C_HOOGTE + 0.0006, 0]} rotation={[-Math.PI / 2, 0, 0]} material={m.gat}>
          <circleGeometry args={[0.075, 32]} />
        </mesh>
        {GATEN.flatMap(([x, z]) => [-0.012, 0.012].map((d) => (
          <mesh key={`${x}${z}${d}`} position={[x + d, C_HOOGTE + 0.0006, z]} rotation={[-Math.PI / 2, 0, 0]} material={m.gat}>
            <circleGeometry args={[0.006, 10]} />
          </mesh>
        )))}
        {/* Typeplaatje bij de kop. */}
        <Tussen a={[0.22, C_HOOGTE, 0.36]} b={[0.34, C_HOOGTE + 0.001, 0.42]} m={m.chassis} schaduw={false} />
      </group>
    </group>
  )
}
